/**
 * **Qual das duas formas uma cura confirmada produz** (gate V28G.U).
 *
 * ## Porque é um módulo e não um `if` dentro da cena
 *
 * Porque a decisão tem quatro entradas e três saídas, e nenhuma delas precisa de
 * um contexto WebGL para ser verificada. Uma cena é wiring; o que erra é a
 * bookkeeping - um feixe desenhado numa autocura, um brilho na nave errada, uma
 * apresentação para uma cura de zero - e isso prova-se num objeto simples.
 *
 * É a mesma razão pela qual `heal-numbers.ts` e `heal-tally.ts` não conhecem uma
 * cena.
 *
 * ## O que ele NÃO decide
 *
 * Nada de arte. Não sabe uma cor, uma duração, uma espessura, um alpha nem uma
 * escala - isso é de `vital-heal-flash.ts`, e continua lá. Este módulo responde
 * a "o quê e sobre quem", e o outro responde a "com que aspeto".
 *
 * E não decide se houve cura: isso é da autoridade, e chega como
 * `appliedMilli`.
 */

/** O que a cena tem de desenhar por causa desta cura. */
export type VitalHealPresentation =
  | {
      /** Nada. Uma cura de zero, ou um casco que este cliente nunca desenhou. */
      readonly kind: 'none';
    }
  | {
      /** Um brilho na silhueta de quem foi curado. Autocura, e só. */
      readonly kind: 'glow';
      readonly hull: string;
    }
  | {
      /** Um feixe entre as bordas de dois cascos. Cura de aliado, e só. */
      readonly kind: 'beam';
      readonly from: string;
      readonly to: string;
    };

const NONE: VitalHealPresentation = Object.freeze({ kind: 'none' });

/**
 * A decisão, e é UMA por facto autoritativo.
 *
 * As três regras, por ordem:
 *
 * 1. **zero não desenha.** A autoridade não anuncia uma cura de zero, e esta é a
 *    segunda vez que isso é dito - do lado que desenha. Sem ela um zero acenderia
 *    forma sem número, porque `healNumbersFrom` descarta um total não positivo;
 * 2. **curador igual a curado é um BRILHO, nunca um feixe.** Uma autocura não tem
 *    duas pontas: um feixe de comprimento zero desenha nada, e inventar-lhe uma
 *    direção seria mentir sobre para onde a energia foi;
 * 3. **caso contrário é um FEIXE**, do curador para o curado. Nunca os dois: a
 *    nave que ativou não recebeu vida nenhuma, e acendê-la diria que recebeu.
 *
 * Um casco que este cliente nunca desenhou não tem silhueta nem borda, e aí não
 * desenhar é a única resposta honesta - `drawn` é quem sabe isso.
 *
 * @param healer quem ativou. Identidade autoritativa, nunca uma posição.
 * @param target quem foi curado. A mesma coisa, e é ele que leva o `+TOTAL`.
 * @param appliedMilli a vida REALMENTE acrescentada, em milli. Nunca recalculada.
 * @param drawn os cascos que esta cena está a desenhar neste instante.
 */
export function vitalHealPresentation(input: {
  readonly healer: string;
  readonly target: string;
  readonly appliedMilli: number;
  readonly drawn: ReadonlySet<string>;
}): VitalHealPresentation {
  const { healer, target, appliedMilli, drawn } = input;
  if (!Number.isFinite(appliedMilli) || appliedMilli <= 0) {
    return NONE;
  }
  if (healer === target) {
    return drawn.has(target) ? Object.freeze({ kind: 'glow' as const, hull: target }) : NONE;
  }
  if (!drawn.has(healer) || !drawn.has(target)) {
    return NONE;
  }
  return Object.freeze({ kind: 'beam' as const, from: healer, to: target });
}

/**
 * Quem leva o `+TOTAL` verde: **sempre o CURADO**.
 *
 * Uma função de uma linha, e existe porque o sentido é o inverso do Roubo de
 * Vida - lá o curado é o autor - e um canal partilhado que entrega os dois ids
 * é um canal onde é fácil ler o errado. O número aparece sobre a barra que sobe.
 *
 * `null` quando não há número a mostrar, pela mesma regra da forma: um total não
 * positivo não é um facto.
 */
export function vitalHealNumberOwner(target: string, appliedMilli: number): string | null {
  return Number.isFinite(appliedMilli) && appliedMilli > 0 ? target : null;
}
